/* eslint-disable camelcase */
const profileTranslations = {
    en: {
        title: 'Profile',
        description: 'View and update your personal information',

        edit: {
            title: 'Profile',
            description: 'Update your personal information and preferences',
            error: 'Could not save your profile. Please try again.',

            sections: {
                communicationPreferences: {
                    title: 'Communication Preferences',
                    subtitle: 'What updates would you like to receive?',
                    unsubscribedInfo: 'You are currently unsubscribed from all email communication. Turn on “All emails” to start receiving emails again.',
                    allEmails: {
                        title: 'All emails',
                        description: 'Receive emails from us. Turn this on to manage the options below.'
                    },
                    reconsent: {
                        label: 'I confirm I have agreed to receive emails again',
                        error: 'Please confirm you have agreed to receive emails again'
                    }
                }
            }
        },

        view: {
            title: 'Profile',
            description: 'View and update your personal information',
            editButton: 'Edit',

            sections: {
                personalInformation: 'Personal Information',
                contactDetails: 'Contact Details',
                professionalDetails: 'Professional Details',
                communicationPreferences: 'Communication Preferences',
                roles: 'Roles',
                skills: 'Skills'
            },

            status: {
                enabled: 'Enabled',
                disabled: 'Disabled'
            },

            communicationPreferences: {
                allEmails: {
                    title: 'All emails',
                    description: 'Whether you receive any email communication from us'
                },
                engagementCampaign: {
                    title: 'New job opportunities',
                    description: 'Get notified about company information and new jobs matching your profile'
                },
                candidateAvailabilityRequests: {
                    title: 'Availability reminders',
                    description: 'Reminders to update your availability'
                },
                candidateNotificationAboutOrders: {
                    title: 'Job-related messages',
                    description: 'Important messages related to specific jobs (new opportunities, assignment confirmations, etc.)'
                },
                candidateJobPreferencesUpdateRequest: {
                    title: 'Job preferences update requests',
                    description: 'Requests and reminders to update job preferences'
                }
            }
        },

        fields: {
            fullName: 'Full Name',
            firstName: 'First Name',
            lastName: 'Last Name',
            email: 'Email',
            mobilePhone: 'Mobile Phone',
            location: 'Location',
            roles: 'Roles',
            rolesSpecialties: 'Roles & specialties',
            skills: 'Skills',
            communicationPreferences: {
                engagementCampaign: 'New job opportunities and company information',
                candidateAvailabilityRequests: 'Requests and reminders to update availability',
                candidateNotificationAboutOrders: 'Important messages related to specific jobs (new opportunities, assignment confirmations, etc.)',
                candidateJobPreferencesUpdateRequest: 'Requests and reminders to update job preferences'
            }
        },

        success: {
            saved: 'Profile updated successfully'
        },

        error: {
            loadFailed: 'Failed to load profile data. Please try again.',
            saveFailed: 'Failed to save profile changes. Please try again.'
        },

        deleteRequest: {
            button: 'Request Data Deletion',
            submitting: 'Submitting request...',
            confirmDialog: {
                title: 'Request Data Deletion?',
                message: 'Are you sure you want to request deletion of your personal data? This action will notify our team to process your request.',
                confirm: 'Yes, Request Deletion',
                cancel: 'Cancel'
            },
            successDialog: {
                title: 'Request Submitted',
                message: 'Your deletion request has been submitted successfully. A recruiter will review and process your request shortly.',
                close: 'Close'
            },
            error: {
                title: 'Request Failed',
                message: 'Failed to submit deletion request. Please try again later.',
                close: 'Close'
            }
        },

        completion: {
            title: 'Profile <bold>{{percentage}}%</bold> complete',
            tips: {
                firstName: 'Add your first name to complete your profile',
                lastName: 'Add your last name to complete your profile',
                mobilePhone: 'Add your phone number to receive important updates',
                location: 'Add your location to get job matches near you',
                roles: 'Add your professional roles to get relevant job opportunities',
                skills: 'Add your skills to help recruiters find you'
            }
        }
    },
    no: {
        title: 'Profil',
        description: 'Vis og oppdater din personlige informasjon',
        editButton: 'Rediger',

        edit: {
            title: 'Rediger Profil',
            description: 'Oppdater din personlige informasjon og preferanser',
            error: 'Kunne ikke lagre profilen din. Vennligst prøv igjen.',

            sections: {
                communicationPreferences: {
                    title: 'Kommunikasjonspreferanser',
                    subtitle: 'Hvilke oppdateringer ønsker du å motta?',
                    unsubscribedInfo: 'Du er for øyeblikket avmeldt all e-postkommunikasjon. Slå på «Alle e-poster» for å begynne å motta e-poster igjen.',
                    allEmails: {
                        title: 'Alle e-poster',
                        description: 'Motta e-poster fra oss. Slå på dette for å administrere alternativene nedenfor.'
                    },
                    reconsent: {
                        label: 'Jeg bekrefter at jeg har samtykket til å motta e-poster igjen',
                        error: 'Vennligst bekreft at du har samtykket til å motta e-poster igjen'
                    }
                }
            }
        },

        view: {
            title: 'Profil',
            description: 'Vis og oppdater din personlige informasjon',
            editButton: 'Rediger',
            sections: {
                personalInformation: 'Personlig informasjon',
                contactDetails: 'Kontaktopplysninger',
                professionalDetails: 'Profesjonelle detaljer',
                communicationPreferences: 'Kommunikasjonspreferanser',
                roles: 'Roller',
                skills: 'Ferdigheter'
            },

            status: {
                enabled: 'Aktivert',
                disabled: 'Deaktivert'
            },

            communicationPreferences: {
                allEmails: {
                    title: 'Alle e-poster',
                    description: 'Om du mottar noen e-postkommunikasjon fra oss'
                },
                engagementCampaign: {
                    title: 'Nye jobbmuligheter',
                    description: 'Bli varslet om selskapsinformasjon og nye jobber som passer profilen din'
                },
                candidateAvailabilityRequests: {
                    title: 'Tilgjengelighetspåminnelser',
                    description: 'Påminnelser om å oppdatere tilgjengeligheten din'
                },
                candidateNotificationAboutOrders: {
                    title: 'Jobbrelaterte meldinger',
                    description: 'Viktige meldinger knyttet til spesifikke jobber (nye muligheter, oppdragsbekreftelser, etc.)'
                },
                candidateJobPreferencesUpdateRequest: {
                    title: 'Forespørsler om jobbpreferanser',
                    description: 'Forespørsler og påminnelser om å oppdatere jobbpreferanser'
                }
            }
        },

        fields: {
            fullName: 'Navn',
            firstName: 'Fornavn',
            lastName: 'Etternavn',
            email: 'E-post',
            mobilePhone: 'Mobiltelefon',
            location: 'Lokasjon',
            rolesSpecialties: 'Roller og spesialiteter',
            communicationPreferences: {
                engagementCampaign: 'Nye jobbmuligheter og informasjon fra selskapet',
                candidateAvailabilityRequests: 'Forespørsler og påminnelser om å oppdatere tilgjengelighet',
                candidateNotificationAboutOrders: 'Viktige meldinger knyttet til konkrete jobber (nye muligheter, oppdragsbekreftelser osv.)',
                candidateJobPreferencesUpdateRequest: 'Forespørsler og påminnelser om å oppdatere jobbpreferanser'
            }
        },

        success: {
            saved: 'Profil oppdatert'
        },

        error: {
            loadFailed: 'Kunne ikke laste profildata. Vennligst prøv igjen.',
            saveFailed: 'Kunne ikke lagre profilendringer. Vennligst prøv igjen.'
        },

        deleteRequest: {
            button: 'Be om sletting av data',
            submitting: 'Sender forespørsel...',
            confirmDialog: {
                title: 'Be om sletting av data?',
                message: 'Er du sikker på at du vil be om sletting av dine personlige data? Dette vil varsle teamet vårt om å behandle forespørselen din.',
                confirm: 'Ja, be om sletting',
                cancel: 'Avbryt'
            },
            successDialog: {
                title: 'Forespørsel sendt',
                message: 'Din slettingsforespørsel har blitt sendt. En rekrutterer vil gjennomgå og behandle forespørselen din snart.',
                close: 'Lukk'
            },
            error: {
                title: 'Forespørsel mislyktes',
                message: 'Kunne ikke sende slettingsforespørsel. Vennligst prøv igjen senere.',
                close: 'Lukk'
            }
        },

        completion: {
            title: 'Profil <bold>{{percentage}}%</bold> fullført',
            tips: {
                firstName: 'Legg til fornavnet ditt for å fullføre profilen',
                lastName: 'Legg til etternavnet ditt for å fullføre profilen',
                mobilePhone: 'Legg til telefonnummeret ditt for å motta viktige oppdateringer',
                location: 'Legg til plasseringen din for å få jobbmatcher i nærheten',
                roles: 'Legg til dine profesjonelle roller for å få relevante jobbmuligheter',
                skills: 'Legg til ferdighetene dine for å hjelpe rekrutterere med å finne deg'
            }
        }
    },
    sv: {
        title: 'Profil',
        description: 'Visa och uppdatera din personliga information',

        edit: {
            title: 'Redigera Profil',
            description: 'Uppdatera din personliga information och inställningar',
            error: 'Det gick inte att spara din profil. Försök igen.',

            sections: {
                communicationPreferences: {
                    title: 'Kommunikationspreferenser',
                    subtitle: 'Vilka uppdateringar vill du ta emot?',
                    unsubscribedInfo: 'Du är för närvarande avregistrerad från all e-postkommunikation. Slå på ”Alla e-postmeddelanden” för att börja ta emot e-post igen.',
                    allEmails: {
                        title: 'Alla e-postmeddelanden',
                        description: 'Ta emot e-post från oss. Slå på detta för att hantera alternativen nedan.'
                    },
                    reconsent: {
                        label: 'Jag bekräftar att jag har samtyckt till att ta emot e-post igen',
                        error: 'Bekräfta att du har samtyckt till att ta emot e-post igen'
                    }
                }
            }
        },

        view: {
            title: 'Profil',
            description: 'Visa och uppdatera din personliga information',
            editButton: 'Redigera',

            sections: {
                personalInformation: 'Personlig information',
                contactDetails: 'Kontaktuppgifter',
                professionalDetails: 'Professionella detaljer',
                communicationPreferences: 'Kommunikationspreferenser',
                roles: 'Roller',
                skills: 'Färdigheter'
            },

            status: {
                enabled: 'Aktiverad',
                disabled: 'Inaktiverad'
            },

            communicationPreferences: {
                allEmails: {
                    title: 'Alla e-postmeddelanden',
                    description: 'Om du tar emot någon e-postkommunikation från oss'
                },
                engagementCampaign: {
                    title: 'Nya jobbmöjligheter',
                    description: 'Bli meddelad om företagsinformation och nya jobb som matchar din profil'
                },
                candidateAvailabilityRequests: {
                    title: 'Tillgänglighetspåminnelser',
                    description: 'Påminnelser om att uppdatera din tillgänglighet'
                },
                candidateNotificationAboutOrders: {
                    title: 'Jobbrelaterade meddelanden',
                    description: 'Viktiga meddelanden kopplade till specifika jobb (nya möjligheter, uppdragsbekräftelser, etc.)'
                },
                candidateJobPreferencesUpdateRequest: {
                    title: 'Förfrågningar om jobbpreferenser',
                    description: 'Förfrågningar och påminnelser om att uppdatera jobbpreferenser'
                }
            }
        },

        fields: {
            fullName: 'Namn',
            firstName: 'Förnamn',
            lastName: 'Efternamn',
            email: 'E-post',
            mobilePhone: 'Mobiltelefon',
            location: 'Plats',
            rolesSpecialties: 'Roller och specialiteter',
            communicationPreferences: {
                engagementCampaign: 'Nya jobbmöjligheter och information från företaget',
                candidateAvailabilityRequests: 'Förfrågningar och påminnelser om att uppdatera tillgänglighet',
                candidateNotificationAboutOrders: 'Viktiga meddelanden kopplade till specifika jobb (nya möjligheter, uppdragsbekräftelser osv.)',
                candidateJobPreferencesUpdateRequest: 'Förfrågningar och påminnelser om att uppdatera jobbpreferenser'
            }
        },

        success: {
            saved: 'Profil uppdaterad'
        },

        error: {
            loadFailed: 'Kunde inte ladda profildata. Vänligen försök igen.',
            saveFailed: 'Kunde inte spara profiländringar. Vänligen försök igen.'
        },

        deleteRequest: {
            button: 'Begär radering av data',
            submitting: 'Skickar begäran...',
            confirmDialog: {
                title: 'Begär radering av data?',
                message: 'Är du säker på att du vill begära radering av dina personuppgifter? Detta kommer att meddela vårt team att behandla din begäran.',
                confirm: 'Ja, begär radering',
                cancel: 'Avbryt'
            },
            successDialog: {
                title: 'Begäran skickad',
                message: 'Din begäran om radering har skickats. En rekryterare kommer att granska och behandla din begäran snart.',
                close: 'Stäng'
            },
            error: {
                title: 'Begäran misslyckades',
                message: 'Kunde inte skicka raderingsbegäran. Vänligen försök igen senare.',
                close: 'Stäng'
            }
        },

        completion: {
            title: 'Profil <bold>{{percentage}}%</bold> klar',
            tips: {
                firstName: 'Lägg till ditt förnamn för att slutföra din profil',
                lastName: 'Lägg till ditt efternamn för att slutföra din profil',
                mobilePhone: 'Lägg till ditt telefonnummer för att få viktiga uppdateringar',
                location: 'Lägg till din plats för att få jobbmatchningar nära dig',
                roles: 'Lägg till dina yrkesroller för att få relevanta jobbmöjligheter',
                skills: 'Lägg till dina färdigheter för att hjälpa rekryterare att hitta dig'
            }
        }
    }
} as const;

export default profileTranslations;
